// The Lumen data table (revamp PR 4): TanStack Table v8 on the Lumen table, pagination, select, checkbox
// and skeleton. It replaces SharedDataTable and mantine-datatable page by page. The column type keeps
// mantine's names (accessor, title, render(record, index), width, textAlign), so a page converts by
// changing the import and keeping its column array. The full recipe is in
// docs/handoffs/2026-10-26-tables/t1-report.md (Lumen Intelligence workspace repo).
//
// Usage (a server-paginated list page):
//   import { DataTable, DataTableToolbar, TableSearch, usePagination, type DataTableColumn } from '…/data-table';
//
//   const { page, pageSize, setPage, setPageSize, resetPage } = usePagination(); // size change → page 1
//   const columns: DataTableColumn<EmailDTO>[] = [
//     { accessor: 'email', title: t('…'), width: 250, mono: true },
//     { accessor: 'isActive', title: t('…'), render: (r) => <Badge variant={r.isActive ? 'ok' : 'err'}>…</Badge> },
//     { accessor: 'actions', title: t('…'), textAlign: 'center', render: (r) => <RowActions record={r} /> },
//   ];
//   <DataTable
//     aria-label={t('menu.email')}
//     columns={columns}
//     data={data}
//     loading={loading}
//     totalRecords={totalCount}
//     currentPage={page}
//     pageSize={pageSize}
//     onPageChange={setPage}
//     onPageSizeChange={setPageSize}
//     toolbar={<DataTableToolbar search={<TableSearch value={q} onChange={(v) => { setQ(v); resetPage(); }} />} />}
//   />
//
// Also: `localPagination` (slice `data` here), `selectedRecords` + `onSelectedRecordsChange` + `bulkActions`
// (checkbox column and selection bar), `onRowClick` (keyboard-reachable rows), `sortStatus` +
// `onSortStatusChange` (`sortable` columns; the page sorts), `columnVisibility` (useColumnVisibility),
// `emptyState`, `rowClassName`, `isRecordHighlighted`, `pinLastColumn` (default on), `framed={false}` inside
// a card. Density comes from DEFAULT_TABLE_DENSITY in density.ts; pages never pass `density`. Identifier
// columns set `mono: true`; an identifier inside a mixed cell goes in <DataTableMono>.
import {
  type CellContext,
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table';
import { ArrowDownIcon, ArrowUpIcon, ChevronsUpDownIcon } from 'lucide-react';
import type * as React from 'react';
import { isValidElement, useEffect, useRef, useState } from 'react';

import { Checkbox } from '@/components/lumen/checkbox';
import { Skeleton } from '@/components/lumen/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/lumen/table';
import { useLanguage } from '@/contexts/LanguageContext';
import { formatTemplate } from '@/lib/format-template';
import { cn } from '@/lib/utils';
import { DataTablePagination } from './data-table-pagination';
import { DEFAULT_TABLE_DENSITY, DENSITY } from './density';
import { DataTableDensityContext } from './mono';
import {
  PAGE_SIZES,
  createRowKeyGetter,
  getColumnIds,
  getPageCount,
  getPageSelectionState,
  getRecordRange,
  getValueAtPath,
  humanize,
  nextSortStatus,
  normalizePageSizeOptions,
  slicePage,
  togglePageSelection,
  toggleRecordSelection,
} from './table-logic';
import { DataTableSelectionBar } from './toolbar';
import type { DataTableColumn, DataTableProps, DataTableTextAlign } from './types';

const SELECT_COLUMN_ID = '__select';
const SELECT_COLUMN_WIDTH = 44;
const DEFAULT_MIN_HEIGHT = 200;
const DEFAULT_MAX_HEIGHT = 'max(20rem, calc(100dvh - 16rem))';
const MAX_SKELETON_ROWS = 10;
const SKELETON_WIDTHS = ['w-3/4', 'w-1/2', 'w-2/3', 'w-5/6'];

const ALIGN: Record<DataTableTextAlign, string> = { left: 'text-left', center: 'text-center', right: 'text-right' };
const JUSTIFY: Record<DataTableTextAlign, string> = {
  left: 'justify-start',
  center: 'justify-center',
  right: 'justify-end',
};
const SKELETON_ALIGN: Record<DataTableTextAlign, string> = { left: '', center: 'mx-auto', right: 'ml-auto' };

// The pinned last column needs an opaque background, so each row state is repainted on it: hover as a tint
// image over the base colour, selected and highlighted as opaque mixes. The shadow shows while there is
// more table to the right.
const PINNED_SHADOW =
  'group-data-[pinned-shadow]/scroll:shadow-[inset_1px_0_0_var(--lm-line),-8px_0_10px_-8px_color-mix(in_srgb,var(--lm-fg)_30%,transparent)]';
const PINNED_CELL = cn(
  'sticky right-0 z-[1] bg-raised',
  'group-hover/row:bg-[linear-gradient(var(--lm-tint),var(--lm-tint))]',
  'group-data-[state=selected]/row:bg-surface-2',
  'group-data-[highlighted]/row:bg-[color-mix(in_srgb,var(--lm-label)_10%,var(--lm-raised))]',
  PINNED_SHADOW
);
const PINNED_HEAD = cn('right-0 z-20', PINNED_SHADOW);

// Clicks that start on a control inside the row are the control's, not the row's.
const INTERACTIVE =
  'a, button, input, select, textarea, label, [role="button"], [role="checkbox"], [role="switch"], [role="combobox"], [role="menuitem"], [contenteditable="true"]';

function isOwnClick(event: React.SyntheticEvent<HTMLTableRowElement>): boolean {
  const row = event.currentTarget;
  const target = event.target as Element | null;
  // React bubbles events from portalled content (a menu opened from the row) through the row.
  if (!target || !row.contains(target)) return false;
  const control = target.closest(INTERACTIVE);
  return !control || control === row || !row.contains(control);
}

function widthStyle(width: number | string | undefined): React.CSSProperties | undefined {
  return width === undefined ? undefined : { width, minWidth: width, maxWidth: width };
}

/** What mantine renders for a column without `render`: strings and numbers, nothing for null or booleans. */
function renderValue(value: unknown): React.ReactNode {
  if (value === null || value === undefined || typeof value === 'boolean') return null;
  if (typeof value === 'string' || typeof value === 'number') return value;
  if (isValidElement(value)) return value;
  return String(value);
}

function useScrollState(ref: React.RefObject<HTMLDivElement | null>) {
  const [state, setState] = useState({ overflow: false, moreRight: false });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const overflowX = el.scrollWidth > el.clientWidth + 1;
      const overflow = overflowX || el.scrollHeight > el.clientHeight + 1;
      const moreRight = overflowX && el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
      setState((prev) => (prev.overflow === overflow && prev.moreRight === moreRight ? prev : { overflow, moreRight }));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    const table = el.querySelector('table');
    if (table) observer.observe(table);
    el.addEventListener('scroll', measure, { passive: true });
    return () => {
      observer.disconnect();
      el.removeEventListener('scroll', measure);
    };
  }, [ref]);
  return state;
}

/** Every column's cell: one stable component type. An inline arrow here was a new component per render, so TanStack's
 *  flexRender remounted every cell on every table render (scrolling sideways re-renders it via useScrollState),
 *  closing open popovers and dropping typed text. */
function ColumnCell<T>({ column, row, getValue }: CellContext<T, unknown>) {
  const render = (column.columnDef.meta as { render?: DataTableColumn<T>['render'] } | undefined)?.render;
  return <>{render ? render(row.original, row.index) : renderValue(getValue())}</>;
}

export function DataTable<T>({
  data,
  columns,
  loading = false,
  pageSize,
  currentPage,
  totalRecords,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions,
  localPagination = false,
  paginationText,
  toolbar,
  framed = true,
  minHeight = DEFAULT_MIN_HEIGHT,
  maxHeight = DEFAULT_MAX_HEIGHT,
  pinLastColumn = true,
  emptyState,
  noRecordsText,
  rowClassName,
  isRecordHighlighted,
  'aria-label': ariaLabel,
  className,
  getRowKey: getRowKeyProp,
  onRowClick,
  rowLabel,
  selectedRecords,
  onSelectedRecordsChange,
  isRecordSelectable,
  bulkActions,
  sortStatus,
  onSortStatusChange,
  columnVisibility,
  density = DEFAULT_TABLE_DENSITY,
}: DataTableProps<T>) {
  const { t } = useLanguage();
  const d = DENSITY[density];
  const [fallbackRowKey] = useState(() => createRowKeyGetter<T>());
  const getRowKey = getRowKeyProp ?? fallbackRowKey;
  const scrollRef = useRef<HTMLDivElement>(null);
  const scroll = useScrollState(scrollRef);

  // Like the old wrapper, the table keeps the page size itself, so the select also works for a
  // `localPagination` table whose page passes no `onPageSizeChange`.
  const [ownPageSize, setOwnPageSize] = useState(pageSize ?? PAGE_SIZES[0]);
  useEffect(() => {
    if (pageSize !== undefined) setOwnPageSize(pageSize);
  }, [pageSize]);
  const size = ownPageSize;
  const paginated = currentPage !== undefined && pageSize !== undefined;
  const page = currentPage ?? 1;
  const rows = paginated && localPagination ? slicePage(data, page, size) : data;
  const total = localPagination ? data.length : totalRecords ?? data.length;

  const selectable = selectedRecords !== undefined && onSelectedRecordsChange !== undefined;
  const selected = selectedRecords ?? [];
  const canSelect = isRecordSelectable ?? (() => true);
  const selectedKeys = new Set(selected.map(getRowKey));
  const pageSelection = selectable ? getPageSelectionState(selected, rows, getRowKey, canSelect) : 'none';
  const sortingOn = sortStatus !== undefined && onSortStatusChange !== undefined;
  const clickable = onRowClick !== undefined;

  // TanStack owns the column model, visibility and row ids; rendering stays here so the React Compiler
  // (which skips components that call useReactTable) never memoises a read of the mutable table object.
  const ids = getColumnIds(columns);
  const columnById = new Map<string, DataTableColumn<T>>(ids.map((id, i) => [id, columns[i]]));
  const visibilityState: VisibilityState = {};
  columns.forEach((column, i) => {
    const accessor = String(column.accessor);
    const fallback = column.toggleable ? column.defaultToggle ?? true : true;
    if (column.hidden || !(columnVisibility?.[accessor] ?? fallback)) visibilityState[ids[i]] = false;
  });

  const columnDefs: ColumnDef<T>[] = columns.map((column, i) => ({
    id: ids[i],
    accessorFn: (record: T) => getValueAtPath(record, String(column.accessor)),
    header: () => column.title ?? humanize(String(column.accessor)),
    cell: ColumnCell as ColumnDef<T>['cell'],
    meta: { render: column.render },
  }));
  if (selectable) {
    columnDefs.unshift({
      id: SELECT_COLUMN_ID,
      header: () => (
        <Checkbox
          checked={pageSelection === 'all' ? true : pageSelection === 'some' ? 'indeterminate' : false}
          disabled={!rows.some(canSelect)}
          onCheckedChange={() => onSelectedRecordsChange?.(togglePageSelection(selected, rows, getRowKey, canSelect))}
          aria-label={t('lumen.table.selectAll')}
        />
      ),
      cell: ({ row }) => {
        const name = rowLabel?.(row.original);
        return (
          <Checkbox
            checked={selectedKeys.has(row.id)}
            disabled={!canSelect(row.original, row.index)}
            onCheckedChange={() => onSelectedRecordsChange?.(toggleRecordSelection(selected, row.original, getRowKey))}
            aria-label={name ? formatTemplate(t('lumen.table.selectRowNamed'), { name }) : t('lumen.table.selectRow')}
          />
        );
      },
    });
  }

  const table = useReactTable({
    data: rows,
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (record) => getRowKey(record),
    state: { columnVisibility: visibilityState },
    manualPagination: true,
    manualSorting: true,
  });

  const leafColumns = table.getVisibleLeafColumns();
  const columnCount = Math.max(leafColumns.length, 1);
  const lastColumn = leafColumns[leafColumns.length - 1];
  const pinnedId =
    pinLastColumn && leafColumns.length > 1 && lastColumn?.id !== SELECT_COLUMN_ID ? lastColumn?.id : undefined;
  const tableRows = table.getRowModel().rows;
  const showSkeleton = loading && rows.length === 0;
  const showEmpty = !loading && rows.length === 0;

  const range = getRecordRange({ page, pageSize: size, totalRecords: total, recordsOnPage: rows.length });
  let rangeText: React.ReactNode;
  if (range) {
    rangeText = paginationText ? (
      paginationText({ from: range.from, to: range.to, totalRecords: total })
    ) : (
      <span className="font-mono text-xs">
        {formatTemplate(t('lumen.table.range'), { from: range.from, to: range.to, total })}
      </span>
    );
  } else {
    rangeText = loading ? t('lumen.table.loading') : noRecordsText ?? t('lumen.table.noRecords');
  }

  const handlePageSize = (next: number) => {
    setOwnPageSize(next);
    onPageSizeChange?.(next);
  };

  const topBar =
    selectable && bulkActions && selected.length > 0 ? (
      <DataTableSelectionBar count={selected.length} onClear={() => onSelectedRecordsChange?.([])}>
        {bulkActions}
      </DataTableSelectionBar>
    ) : (
      toolbar
    );

  const columnAlign = (id: string): DataTableTextAlign => columnById.get(id)?.textAlign ?? 'left';
  const columnWidth = (id: string) =>
    id === SELECT_COLUMN_ID ? widthStyle(SELECT_COLUMN_WIDTH) : widthStyle(columnById.get(id)?.width);

  return (
    <DataTableDensityContext.Provider value={density}>
      <div
        data-slot="data-table"
        data-density={density}
        className={cn(
          'flex min-w-0 flex-col bg-raised text-fg',
          framed && 'overflow-hidden rounded-card border border-line',
          className
        )}
      >
        {topBar}
        {/* Scrolls both ways under the sticky header. While it overflows it is a focusable region, so the
          keyboard can scroll it (WCAG 2.1.1). */}
        <div
          ref={scrollRef}
          data-slot="data-table-scroll"
          data-pinned-shadow={(pinnedId && scroll.moreRight) || undefined}
          role={scroll.overflow && ariaLabel ? 'region' : undefined}
          aria-label={scroll.overflow ? ariaLabel : undefined}
          tabIndex={scroll.overflow ? 0 : undefined}
          className="group/scroll relative min-h-0 overflow-auto overscroll-x-contain outline-none focus-visible:ring-[3px] focus-visible:ring-label/25 focus-visible:ring-inset [&>[data-slot=table-container]]:overflow-visible"
          style={{ minHeight, maxHeight: maxHeight === 'none' ? undefined : maxHeight }}
        >
          <Table
            aria-label={ariaLabel}
            aria-busy={loading || undefined}
            className={cn('border-separate border-spacing-0', d.table)}
          >
            <TableHeader className="bg-transparent">
              {table.getHeaderGroups().map((group) => (
                <TableRow key={group.id} className="hover:bg-transparent">
                  {group.headers.map((header) => {
                    const id = header.column.id;
                    const column = columnById.get(id);
                    const align = columnAlign(id);
                    const sortable = sortingOn && column?.sortable;
                    const direction =
                      sortable && sortStatus?.columnAccessor === column?.accessor ? sortStatus?.direction : undefined;
                    const content = flexRender(header.column.columnDef.header, header.getContext());
                    return (
                      <TableHead
                        key={header.id}
                        scope="col"
                        style={columnWidth(id)}
                        aria-sort={
                          sortable
                            ? direction === 'asc'
                              ? 'ascending'
                              : direction === 'desc'
                                ? 'descending'
                                : 'none'
                            : undefined
                        }
                        className={cn(
                          'sticky top-0 z-10 border-line-strong border-b bg-surface',
                          d.head,
                          ALIGN[align],
                          id === pinnedId && PINNED_HEAD,
                          column?.titleClassName
                        )}
                      >
                        {sortable && column ? (
                          <button
                            type="button"
                            onClick={() =>
                              onSortStatusChange?.(nextSortStatus(sortStatus, column.accessor, column.sortKey))
                            }
                            className={cn(
                              '-mx-1 inline-flex items-center gap-1 rounded-tag px-1 py-0.5 outline-none hover:text-fg focus-visible:ring-[3px] focus-visible:ring-label/40',
                              direction && 'text-fg',
                              JUSTIFY[align]
                            )}
                          >
                            {content}
                            {direction === 'asc' ? (
                              <ArrowUpIcon aria-hidden className="size-3.5" />
                            ) : direction === 'desc' ? (
                              <ArrowDownIcon aria-hidden className="size-3.5" />
                            ) : (
                              <ChevronsUpDownIcon aria-hidden className="size-3.5 opacity-60" />
                            )}
                          </button>
                        ) : (
                          content
                        )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody
              className={cn(
                '[&>tr:last-child>td]:border-b-0',
                loading && rows.length > 0 && 'opacity-60 transition-opacity'
              )}
            >
              {showSkeleton &&
                Array.from({ length: Math.min(size, MAX_SKELETON_ROWS) }, (_, r) => (
                  // Placeholder rows: fixed count, never reordered, so the index is a stable key.
                  // biome-ignore lint/suspicious/noArrayIndexKey: see above.
                  <TableRow key={`skeleton-${r}`} className="hover:bg-transparent">
                    {leafColumns.map((leaf, c) => (
                      <TableCell key={leaf.id} style={columnWidth(leaf.id)} className={cn(d.cell, d.rule)}>
                        <Skeleton
                          className={cn(
                            'h-3.5',
                            leaf.id === SELECT_COLUMN_ID ? 'size-4' : SKELETON_WIDTHS[(r + c) % SKELETON_WIDTHS.length],
                            SKELETON_ALIGN[columnAlign(leaf.id)]
                          )}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              {showEmpty && (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={columnCount} className="whitespace-normal p-0">
                    <div
                      className="flex flex-col items-center justify-center gap-1 px-4 py-8 text-center"
                      style={{ minHeight: Math.max(minHeight - d.headPx, 96) }}
                    >
                      {emptyState ?? (
                        <>
                          <p className="font-semibold text-fg-2 text-sm">
                            {noRecordsText ?? t('lumen.table.emptyTitle')}
                          </p>
                          {noRecordsText === undefined && (
                            <p className="text-13 text-fg-muted">{t('lumen.table.emptyDescription')}</p>
                          )}
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {!showSkeleton &&
                tableRows.map((row) => {
                  const record = row.original;
                  const index = row.index;
                  const isSelected = selectable && selectedKeys.has(row.id);
                  const highlighted = isRecordHighlighted?.(record) ?? false;
                  return (
                    <TableRow
                      key={row.id}
                      data-state={isSelected ? 'selected' : undefined}
                      data-highlighted={highlighted || undefined}
                      // A clickable row is focusable and answers Enter / Space (REVIEW.md "Always check" 3);
                      // it keeps its row role so the table stays a table for screen readers.
                      tabIndex={clickable ? 0 : undefined}
                      onClick={
                        clickable
                          ? (event) => {
                              if (isOwnClick(event)) onRowClick?.({ record, index, event });
                            }
                          : undefined
                      }
                      onKeyDown={
                        clickable
                          ? (event) => {
                              if (event.target !== event.currentTarget) return;
                              if (event.key !== 'Enter' && event.key !== ' ') return;
                              event.preventDefault();
                              onRowClick?.({ record, index, event });
                            }
                          : undefined
                      }
                      className={cn(
                        'group/row',
                        highlighted && 'bg-label/10 hover:bg-label/14',
                        clickable &&
                          'cursor-pointer outline-none focus-visible:outline-2 focus-visible:outline-label focus-visible:-outline-offset-2',
                        rowClassName?.(record, index)
                      )}
                    >
                      {row.getVisibleCells().map((cell) => {
                        const id = cell.column.id;
                        const column = columnById.get(id);
                        const extra =
                          typeof column?.cellsClassName === 'function'
                            ? column.cellsClassName(record, index)
                            : column?.cellsClassName;
                        const content = flexRender(cell.column.columnDef.cell, cell.getContext());
                        return (
                          <TableCell
                            key={cell.id}
                            style={columnWidth(id)}
                            className={cn(
                              d.cell,
                              d.rule,
                              // A sized column wraps inside its width (mantine); an unsized one keeps its
                              // content on one line and the table scrolls sideways instead (direction B).
                              column?.width !== undefined && !column.noWrap
                                ? 'whitespace-normal break-words'
                                : 'whitespace-nowrap',
                              ALIGN[columnAlign(id)],
                              column?.mono && d.mono,
                              highlighted && 'first:shadow-[inset_3px_0_0_var(--lm-label)]',
                              id === pinnedId && PINNED_CELL,
                              extra
                            )}
                          >
                            {column?.ellipsis ? (
                              <span className="block truncate" style={widthStyle(column.width)}>
                                {content}
                              </span>
                            ) : (
                              content
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </div>
        {paginated && (
          <DataTablePagination
            page={page}
            pageCount={getPageCount(total, size)}
            onPageChange={onPageChange}
            pageSize={size}
            pageSizeOptions={normalizePageSizeOptions(pageSizeOptions, size)}
            onPageSizeChange={localPagination || onPageSizeChange ? handlePageSize : undefined}
            rangeText={rangeText}
          />
        )}
      </div>
    </DataTableDensityContext.Provider>
  );
}
